import { MDXProvider } from '@mdx-js/react'
import { useLocation } from '@reach/router'
import { Blockquote } from 'components/BlockQuote'
import SupportQuestions from 'components/SupportQuestions'
import { Heading } from 'components/Heading'
import { InlineCode } from 'components/InlineCode'
import Layout from 'components/Layout'
import Link from 'components/Link'
import PostLayout from 'components/PostLayout'
import Contributors from 'components/PostLayout/Contributors'
import ShareLinks from 'components/PostLayout/ShareLinks'
import SidebarSection from 'components/PostLayout/SidebarSection'
import Topics from 'components/PostLayout/Topics'
import { SEO } from 'components/seo'
import { SEO2 } from 'components/seo2'
import { ZoomImage } from 'components/ZoomImage'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import { graphql } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { animateScroll as scroll } from 'react-scroll'
import { shortcodes } from '../../mdxGlobalComponents'
import slugify from 'slugify'
import { MdxCodeBlock } from 'components/CodeBlock'
import MobileSidebar from 'components/Docs/MobileSidebar'
import { Intro } from '../../templates/BlogPost'
import TutorialsSlider from 'components/TutorialsSlider'

export const ViewButton = ({ title, view, setView }) => {
    return (
        <button
            onClick={() => setView(title)}
            className={`relative border-b-2 py-2 px-4 text-sm font-medium transition-colors after:absolute after:top-[100%] after:left-0 after:right-0 after:h-[2px] after:rounded-full ${
                view === title
                    ? 'font-bold after:bg-red'
                    : 'border-transparent font-semibold opacity-50 hover:opacity-75 hover:after:bg-gray-accent'
            }`}
            // style={{
            //     background: view === title ? '#0097c2' : '#E5E7E0',
            //     color: view === title ? 'white' : 'black',
            // }}
            // className="py-2 px-4 rounded-md w-1/2 transition-colors"
        >
            {title}
        </button>
    )
}

const A = (props) => <Link {...props} className="font-semibold text-red hover:text-red" />

const TutorialSidebar = ({ contributors, location, title, categories }) => {
    return (
        <>
            {contributors?.length > 0 && (
                <SidebarSection>
                    <Contributors
                        contributors={contributors?.map((contributor) => ({
                            ...contributor,
                        }))}
                    />
                </SidebarSection>
            )}
            <SidebarSection title="Share">
                <ShareLinks title={title} href={location?.href} />
            </SidebarSection>
            {categories?.length > 0 && (
                <SidebarSection title="Filed under...">
                    <Topics
                        topics={categories?.map((category) => ({
                            name: category,
                            url: `/tutorials/categories/${slugify(category, { lower: true })}`,
                        }))}
                    />
                </SidebarSection>
            )}
        </>
    )
}

export default function Tutorial(pageProps) {
    const { data, location, pageContext, params, children } = pageProps
    const { tableOfContents, menu } = pageContext
    const { pageData } = data
    const { body, excerpt, fields, frontmatter, parent } = pageData
    const {
        date,
        featuredImage,
        title,
        description,
        contributors,
        categories,
        name,
        showTitle,
        hideAnchor,
        sidebar,
        slug,
        featuredVideo,
        featuredImageType,
        contributors2,
        tags,
        category,
        breadcrumb,
    } = frontmatter
    const filePath = parent?.relativePath
    const contributorsData = contributors || contributors2
    const featuredImg = featuredImage || parent?.cover?.image
    const components = {
        ...shortcodes(pageProps),
        inlineCode: InlineCode,
        blockquote: Blockquote,
        pre: MdxCodeBlock,
        MultiLanguage: MdxCodeBlock,
        img: ZoomImage,
        h1: (props) => Heading({ as: 'h1', ...props }),
        h2: (props) => Heading({ as: 'h2', ...props }),
        h3: (props) => Heading({ as: 'h3', ...props }),
        h4: (props) => Heading({ as: 'h4', ...props }),
        h5: (props) => Heading({ as: 'h5', ...props }),
        h6: (props) => Heading({ as: 'h6', ...props }),
        a: A,
        TutorialsSlider,
    }
    const breakpoints = useBreakpoint()
    const [view, setView] = useState('Article')

    return (
        <Layout>
            <SEO
                title={title + ''}
                description={description || excerpt}
                article
                image={`/og-images/${fields?.slug?.replace(/\//g, '')}.jpeg`}
            />
            <PostLayout
                questions={<SupportQuestions />}
                body={body}
                featuredImage={featuredImg}
                featuredVideo={featuredVideo}
                tableOfContents={tableOfContents}
                title={title}
                menu={menu}
                sidebar={
                    <TutorialSidebar
                        contributors={contributors}
                        location={location}
                        title={title}
                        categories={categories}
                    />
                }
            >
                <Intro
                    contributors={contributors}
                    featuredImage={featuredImg}
                    title={title}
                    featuredImageType="full"
                    titlePosition="top"
                    date={date}
                />

                {featuredVideo && (
                    <div className="mb-6 flex space-x-2">
                        <ViewButton view={view} title="Article" setView={setView} />
                        <ViewButton view={view} title="Video" setView={setView} />
                    </div>
                )}
                {view === 'Article' && breakpoints.md && <MobileSidebar tableOfContents={tableOfContents} />}
                {view === 'Article' ? (
                    <div className="article-content">
                        <MDXProvider components={components}>
                            <MDXRenderer>{body}</MDXRenderer>
                        </MDXProvider>
                    </div>
                ) : (
                    <iframe src={featuredVideo} />
                )}
            </PostLayout>
        </Layout>
    )
}

export const query = graphql`
    query TutorialLayout($id: String!) {
        pageData: mdx(id: { eq: $id }) {
            body
            excerpt
            fields {
                slug
            }
            frontmatter {
                title
                date
                description
                categories: tags
                contributors2: authorData2 {
                    id
                    name
                    role
                    handle
                    image {
                        childImageSharp {
                            gatsbyImageData(width: 38, height: 38)
                        }
                    }
                }
                contributors: authorData {
                    frontmatter {
                        handle
                        link_type
                        link_url
                        name
                        role: jobTitle
                        image {
                            childrenImageSharp {
                                gatsbyImageData(width: 38, height: 38)
                            }
                        }
                    }
                    id
                    name
                    parent {
                        ... on GoogleDocs {
                            images {
                                name
                                publicURL
                                childImageSharp {
                                    gatsbyImageData
                                }
                            }
                            cover {
                                image {
                                    publicURL
                                    childImageSharp {
                                        gatsbyImageData(width: 38, height: 38)
                                    }
                                }
                            }
                        }
                    }
                }
                featuredVideo
                featuredImage {
                    childImageSharp {
                        gatsbyImageData(placeholder: NONE)
                    }
                }
            }
            parent {
                ... on GoogleDocs {
                    images {
                        name
                        publicURL
                        childImageSharp {
                            gatsbyImageData
                        }
                    }
                    cover {
                        image {
                            publicURL
                            childImageSharp {
                                gatsbyImageData
                            }
                        }
                    }
                }
            }
        }
    }
`
